import { PortfolioExportIdFactory } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportIdFactory";
import { toPortfolioAsset } from "@/tools/aggregate/portfolio-export/utils/asset";

// An asset as written to Supabase: generated id and serialised payload
export interface PortfolioAssetChunk {
  id: string;
  data: string;
}

/**
 * Builds the stream that turns a Portfolio Export upload into Supabase rows.
 */
export const createPortfolioAssetStream = (
  idFactory: PortfolioExportIdFactory,
  regionType?: string
): TransformStream<Uint8Array, PortfolioAssetChunk> => {
  const decoder = new TextDecoder();

  // The CSV header row, taken from the first non-blank line of the file.
  let header = "";

  // Trailing fragment of the input not yet a complete line
  let buffer = "";

  // Generates sequential indexes
  const indexGenerator = (function* () {
    let index = 0;

    while (true) {
      yield index++;
    }
  })();

  // Emits a single complete line.
  // Blank lines are skipped rather than converted.
  const emitLine = async (line: string, controller: TransformStreamDefaultController<PortfolioAssetChunk>) => {
    if (line.trim() === "") return;

    // The first non-blank line of the file is the header.
    if (!header) {
      header = line;
      return;
    }

    const id = await idFactory.get(indexGenerator.next().value);

    controller.enqueue({
      id,
      data: JSON.stringify(toPortfolioAsset(line, header, id, regionType)),
    });
  };

  return new TransformStream<Uint8Array, PortfolioAssetChunk>({
    transform: async (chunk, controller) => {
      buffer += decoder.decode(chunk, { stream: true });

      const lines = buffer.split("\n");

      // The last element is either an incomplete line or "" when the chunk ended
      // exactly on a newline. Either way it is not yet safe to emit, so it goes
      // back into the buffer for the next chunk to complete.
      buffer = lines.pop() ?? "";

      // Sequential and awaited; Preserved line order
      for (const line of lines) {
        await emitLine(line, controller);
      }
    },
    flush: async (controller) => {
      // Final decode flushes any bytes it's holding
      buffer += decoder.decode();

      await emitLine(buffer, controller);
      buffer = "";
    },
  });
};
